import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BACKEND = BACKEND_URL;

/** Resolve image url: if absolute http(s), return as-is; if it starts with /api/files/, prefix backend */
export function resolveImage(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/") || u.startsWith("/uploads/")) return `${BACKEND_URL}${u}`;
  return u;
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ddms_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("ddms_token");
      localStorage.removeItem("ddms_user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function fmtINR(n) {
  const num = Number(n || 0);
  return "₹" + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtDate(d) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d.length <= 10 ? d + "T00:00:00" : d) : d;
  if (isNaN(dt.getTime())) return String(d);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(dt.getDate()).padStart(2,"0")}-${months[dt.getMonth()]}-${dt.getFullYear()}`;
}

export function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export function downloadBlob(url, filename) {
  const token = localStorage.getItem("ddms_token");
  return fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => {
      if (!r.ok) throw new Error("Download failed");
      return r.blob();
    })
    .then(blob => {
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
    });
}

export function formatErr(err) {
  if (!err) return "An unexpected error occurred. Please try again.";
  
  // Handle Axios / HTTP response errors
  if (err?.response) {
    const status = err.response.status;
    const data = err.response.data;

    // Check backend payload message first
    if (data) {
      if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
      if (typeof data.message === "string" && data.message.trim()) return data.message;
      if (typeof data.error === "string" && data.error.trim()) return data.error;

      // Handle FastAPI / Pydantic validation array errors
      if (Array.isArray(data.detail)) {
        const msgs = data.detail.map(e => {
          if (!e) return "";
          if (typeof e === "string") return e;
          if (typeof e.msg === "string") {
            const field = Array.isArray(e.loc) ? e.loc.filter(l => l !== "body" && l !== "query").join(" → ") : "";
            return field ? `${field}: ${e.msg}` : e.msg;
          }
          return JSON.stringify(e);
        }).filter(Boolean);
        if (msgs.length) return msgs.join(" | ");
      }
    }

    // Status code fallbacks
    if (status === 401) return "Invalid username or password. Please check your login credentials and try again.";
    if (status === 403) return "Your account is disabled or you do not have permission to perform this action.";
    if (status === 404) return "Requested resource or endpoint was not found.";
    if (status === 422) return "Unprocessable entity. Please verify input fields.";
    if (status >= 500) return `Server Error (${status}). The backend service encountered an issue. Please try again.`;
    return `HTTP Error ${status}: ${err.response.statusText || "Request failed"}`;
  }

  // Handle client-side network error (backend down / offline)
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return "Network Connection Error. Unable to connect to the backend server. Please ensure the server is running.";
  }

  // Handle direct string input
  if (typeof err === "string" && err.trim()) return err;

  // Handle array input
  if (Array.isArray(err)) {
    const msgs = err.map(e => (typeof e === "string" ? e : e?.msg || e?.message || JSON.stringify(e))).filter(Boolean);
    if (msgs.length) return msgs.join(" | ");
  }

  // Handle standard JS Error object
  if (typeof err === "object") {
    if (typeof err.detail === "string" && err.detail.trim()) return err.detail;
    if (typeof err.message === "string" && err.message.trim()) return err.message;
    if (typeof err.msg === "string" && err.msg.trim()) return err.msg;
    if (typeof err.error === "string" && err.error.trim()) return err.error;
  }

  return String(err);
}
